import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import type { AnnieChatItem } from "../../hooks/useAnnie";
import { FONT_SIZE, INK } from "../../theme/tokens";
import { localizeAnnieDetailParams } from "../../utils/annieDetailParams";
import type { AnniePreflightItem } from "../../utils/annieStream";
import { ChatCard } from "../UIToolkit/Chat/ChatCard";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";
import { AnniePickupTable } from "./AnniePickupTable";

type AnnieProposalItem = Extract<AnnieChatItem, { kind: "proposal" }>;

type Props = {
	item: AnnieProposalItem;
	onApprove: () => void;
	onReject: () => void;
};

/** Terminal states that replace the action buttons with a status line. */
const TERMINAL_NOTE_STATUSES = ["stale", "expired", "failed"] as const;

/**
 * One proposed Annie action awaiting the head nurse's decision: summary,
 * preflight warnings, Approve / Reject. "Edit" is deferred (not rendered in v1).
 * When the backend hard-blocks the action (a block-severity preflight item / no
 * "approve" in actions), it renders a blocked state instead — the reason(s) in
 * full and a single Dismiss, so the block is understood *before* approving.
 */
export const AnnieProposalCard: FC<Props> = ({ item, onApprove, onReject }) => {
	const { t, i18n } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const { card, status, statusNote } = item;

	const preflightLabel = (code: string): string => {
		const key = `annie.preflight.${code}`;
		// Unknown codes fall back to the raw code — it's backend data, not UI copy.
		return i18n.exists(key) ? t(key) : code;
	};

	// Prefer the backend's localized detail_key; fall back to the English detail.
	const preflightDetail = (item: AnniePreflightItem): string =>
		item.detail_key && i18n.exists(item.detail_key) ? t(item.detail_key, localizeAnnieDetailParams(item.detail_params)) : item.detail;

	const showActions = status === "pending" || status === "approving";
	const terminalNote = TERMINAL_NOTE_STATUSES.find((terminal) => terminal === status) ? statusNote : undefined;

	// A hard pre-flight BLOCK (e.g. DOUBLE_BOOK): the backend both drops "approve"
	// from `actions` and attaches a block-severity preflight item — either signal
	// alone is authoritative. When blocked we can't proceed, so the card shows the
	// reason(s) in full and offers dismissal only (no Approve). Terminal states
	// (done/rejected/stale/expired/failed) keep their existing rendering.
	const blockItems = card.preflight.filter((preflightItem) => preflightItem.severity === "block");
	const isBlocked = blockItems.length > 0 || !card.actions.includes("approve");
	const showBlockedState = isBlocked && showActions;
	// Warnings (OT, coverage gap, on-PTO/inactive/…) are proceedable but must be
	// understood BEFORE approving: each reason shows in full (not a hover tooltip),
	// and — when present — the confirm reads "Approve anyway" so the click is an
	// explicit acknowledgement of the flagged consequence.
	const warnItems = card.preflight.filter((preflightItem) => preflightItem.severity === "warn");
	const hasWarnings = warnItems.length > 0;

	return (
		<ChatCard dataTestId={`annie-proposal-card-${card.proposal_id}`} dimmed={status === "rejected"}>
			<Typography sx={{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: INK.MUTED }}>
				{t("annie.proposal.label")}
			</Typography>
			<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, lineHeight: 1.5, color: INK.PRIMARY }}>{card.summary}</Typography>

			{showBlockedState && (
				<Box
					data-testid={`annie-proposal-blocked-${card.proposal_id}`}
					sx={(theme) => ({
						display: "flex",
						gap: 1,
						p: 1.25,
						borderRadius: 1.5,
						borderInlineStart: `3px solid ${theme.palette.error.main}`,
						backgroundColor: theme.palette.error.light,
					})}
				>
					<BlockIcon sx={(theme) => ({ fontSize: 20, color: theme.palette.error.main, mt: "1px", flexShrink: 0 })} />
					<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
						<Typography sx={(theme) => ({ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 700, lineHeight: 1.4, color: theme.palette.error.dark })}>
							{t("annie.proposal.blocked")}
						</Typography>
						{blockItems.map((blockItem) => (
							<Typography
								key={blockItem.code}
								data-testid={`annie-proposal-block-reason-${card.proposal_id}-${blockItem.code}`}
								sx={{ fontSize: FONT_SIZE.SECONDARY, lineHeight: 1.45, color: INK.SECONDARY }}
							>
								{preflightDetail(blockItem)}
							</Typography>
						))}
					</Box>
				</Box>
			)}

			{hasWarnings && (
				<Box
					data-testid={`annie-proposal-warnings-${card.proposal_id}`}
					sx={(theme) => ({
						display: "flex",
						gap: 1,
						p: 1.25,
						borderRadius: 1.5,
						borderInlineStart: `3px solid ${theme.palette.warning.main}`,
						backgroundColor: theme.palette.warning.light,
					})}
				>
					<ErrorOutlineIcon sx={(theme) => ({ fontSize: 20, color: theme.palette.warning.main, mt: "1px", flexShrink: 0 })} />
					<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0 }}>
						{warnItems.map((warnItem) => (
							<Box
								key={warnItem.code}
								data-testid={`annie-proposal-warn-${card.proposal_id}-${warnItem.code}`}
								sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}
							>
								<Typography sx={(theme) => ({ fontSize: FONT_SIZE.SECONDARY, fontWeight: 700, lineHeight: 1.4, color: theme.palette.warning.dark })}>
									{preflightLabel(warnItem.code)}
								</Typography>
								<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, lineHeight: 1.45, color: INK.SECONDARY }}>{preflightDetail(warnItem)}</Typography>
							</Box>
						))}
					</Box>
				</Box>
			)}

			{/* The tiny approve table (Approve verb only) — the per-pickup cost/OT/partial the head nurse
			    approves on, above the actions. Present while the decision is live. */}
			{showActions && card.pickup_table && card.pickup_table.length > 0 && (
				<AnniePickupTable rows={card.pickup_table} timezone={chosenDepartment.timezone} dataTestId={`annie-pickup-table-${card.proposal_id}`} />
			)}

			{showActions &&
				(showBlockedState ? (
					// Blocked: no Approve — the only path forward is to dismiss (reuses onReject).
					<Box sx={{ display: "flex" }}>
						<SystemButton buttonVariant="secondary" size="small" onClick={onReject} data-testid={`annie-proposal-dismiss-${card.proposal_id}`}>
							{t("annie.proposal.dismiss")}
						</SystemButton>
					</Box>
				) : (
					<Box sx={{ display: "flex", gap: 1 }}>
						<SystemButton
							buttonVariant="primary"
							size="small"
							loading={status === "approving"}
							onClick={onApprove}
							data-testid={`annie-proposal-approve-${card.proposal_id}`}
						>
							{hasWarnings ? t("annie.proposal.approve_anyway") : t("annie.proposal.approve")}
						</SystemButton>
						<SystemButton
							buttonVariant="borderless"
							size="small"
							disabled={status === "approving"}
							onClick={onReject}
							data-testid={`annie-proposal-reject-${card.proposal_id}`}
						>
							{t("annie.proposal.reject")}
						</SystemButton>
					</Box>
				))}

			{status === "done" && (
				<Box data-testid={`annie-proposal-done-${card.proposal_id}`} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
					<CheckCircleOutlineIcon sx={{ fontSize: 18, color: "success.main" }} />
					<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: "success.main" }}>{t("annie.proposal.done")}</Typography>
				</Box>
			)}

			{status === "rejected" && (
				<Typography data-testid={`annie-proposal-rejected-${card.proposal_id}`} sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>
					{t("annie.proposal.rejected")}
				</Typography>
			)}

			{terminalNote && (
				<Box data-testid={`annie-proposal-note-${card.proposal_id}`} sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
					<ErrorOutlineIcon sx={{ fontSize: 18, color: status === "stale" ? "warning.main" : "error.main", mt: "1px" }} />
					<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, lineHeight: 1.45, color: INK.SECONDARY }}>{terminalNote}</Typography>
				</Box>
			)}
		</ChatCard>
	);
};
